import React, { useEffect } from 'react';
import { STATUS_CONFIG, formatTime, formatPlate, getConfidenceColor } from '../constants';

export function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { bg: '#F1EFE8', color: '#444441', label: status };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 20,
      letterSpacing: '0.05em', whiteSpace: 'nowrap',
      textTransform: 'uppercase',
    }}>
      {cfg.label}
    </span>
  );
}

export function ConfidenceBar({ score }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = getConfidenceColor(pct);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        flex: 1, height: 6, background: '#F1EFE8',
        borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: color, borderRadius: 3,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <span style={{
        fontSize: 12, fontWeight: 700, color,
        minWidth: 42, fontFamily: 'var(--font-mono)',
      }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

export function KPICard({ label, value, accent, sublabel }) {
  const accents = {
    blue:   { bg: '#E6F1FB', color: '#0C447C', border: '#B5D4F4' },
    amber:  { bg: '#FAEEDA', color: '#633806', border: '#FAC775' },
    green:  { bg: '#EAF3DE', color: '#27500A', border: '#C0DD97' },
    red:    { bg: '#FCEBEB', color: '#791F1F', border: '#F7C1C1' },
    purple: { bg: '#EEEDFE', color: '#3C3489', border: '#CECBF6' },
  };
  const a = accents[accent] || accents.blue;
  return (
    <div style={{
      background: a.bg, border: `1px solid ${a.border}`,
      borderRadius: 14, padding: '18px 16px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{
        fontSize: 32, fontWeight: 700, color: a.color,
        lineHeight: 1, fontFamily: 'var(--font-mono)',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 12, color: a.color, opacity: 0.8,
        fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      {sublabel && (
        <div style={{ fontSize: 11, color: a.color, opacity: 0.55 }}>
          {sublabel}
        </div>
      )}
    </div>
  );
}

export function PlateTag({ plate }) {
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontWeight: 700,
      fontSize: 15, letterSpacing: '0.08em',
      color: 'var(--text-primary)',
    }}>
      {formatPlate(plate)}
    </span>
  );
}

export function TransactionRow({ tx, statusOverride }) {
  const conf = tx.aiConfidenceScore;
  return (
    <div className="fade-in" style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', gap: 12,
      padding: '14px 20px',
      borderBottom: '0.5px solid var(--border)',
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <PlateTag plate={tx.licensePlateString} />
          {tx.manualOverrideApplied && (
            <span style={{
              fontSize: 10, background: '#FAEEDA', color: '#633806',
              padding: '2px 8px', borderRadius: 20, fontWeight: 600,
            }}>
              Manual
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>
          In: {formatTime(tx.entryLoggedAt)}
          {tx.exitLoggedAt && <span> · Out: {formatTime(tx.exitLoggedAt)}</span>}
        </div>
        {(tx.freightClassification || tx.expectedLoad) && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {tx.freightClassification ? (
              <>
                {tx.freightClassification.replace(/_/g, ' ')}
                {conf != null && (
                  <span style={{ color: getConfidenceColor(conf), marginLeft: 6, fontWeight: 600 }}>
                    {Number(conf).toFixed(1)}%
                  </span>
                )}
              </>
            ) : (
              // freightClassification is only set once the AI photo is captured at
              // exit — until then, expectedLoad (declared at entry) is all we know.
              <>
                <span style={{ color: 'var(--text-muted)' }}>Declared:</span>{' '}
                {tx.expectedLoad.replace(/_/g, ' ')}
              </>
            )}
          </div>
        )}
      </div>
      <StatusPill status={statusOverride || tx.lifecycleStatus} />
    </div>
  );
}

export function Spinner({ size = 20, color = 'var(--text-muted)' }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid transparent`,
      borderTop: `2px solid ${color}`,
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      display: 'inline-block',
    }} />
  );
}

export function Toast({ message, type, onClose }) {
  const colors = {
    success: { bg: '#EAF3DE', color: '#27500A', border: '#C0DD97' },
    error:   { bg: '#FCEBEB', color: '#791F1F', border: '#F7C1C1' },
    info:    { bg: '#FAEEDA', color: '#633806', border: '#FAC775' },
  };
  const c = colors[type] || colors.info;

  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%',
      transform: 'translateX(-50%)',
      background: c.bg, color: c.color,
      border: `1px solid ${c.border}`,
      borderRadius: 12, padding: '12px 20px',
      fontSize: 13, fontWeight: 600,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      zIndex: 9999, whiteSpace: 'nowrap',
      animation: 'fadeIn 0.2s ease',
    }}>
      {message}
    </div>
  );
}


